
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { classNames, dispatchModal, displayUserName, useUrlState, wrapClick } from "utils";
import moment from "moment";
import { FC } from "react";
import type { Complaint } from "../schema";
import { gql, useQuery } from "@apollo/client";
import { Avatar, Loader, StatusBadge } from "components";
import { LocationGenerics } from "router/location";
import { useSearch } from "react-location";
import ViewComplaintContainer from "../../complaints/view";

interface ChildComplaintsContainerProps {
  complaint?: Complaint;
}

export const GET_COMPLAINTS = gql`
  query GetComplaints(
    $parent: ID
  ) {
    rows: getComplaints(
      page: 1
      pageSize: 0
      sort: "-createdAt"
      parent: $parent
    ) {
      _id
      code
      description
      contactPerson {
        emailAddress
        fullName
        phoneNumber
      }
      category
      status
      origin
      createdAt
      updatedAt
      createdBy {
        ... on User {
          _id
          code
          firstName
          lastName
          profileImageUrl
        }
        ... on PartnerUser {
          _id
          code
          firstName
          lastName
          profileImageUrl
        }
      }
    }
  }
`;

const ChildComplaintsContainer: FC<ChildComplaintsContainerProps> = ({ complaint }) => {

  const searchParams = useSearch<LocationGenerics>();
  const [modal, setModal] = useUrlState("modal");
  
  const { loading, data } = useQuery(GET_COMPLAINTS, {
    notifyOnNetworkStatusChange: false,
    skip: !complaint?._id,
    variables: {
      parent: complaint?._id,
    },
  });

  return (
    <>
      <div
        className={classNames(
          "bg-white shadow min-h-[50vh] overflow-y-auto"
        )}
      >
       {
        loading ? (
          <div className="flex-1 flex min-h-[50vh] items-center justify-center">
            <Loader title="Loading Complaints..." />
          </div>
        ) : data?.rows?.length > 0 ? (
          <div className="flex-1 p-6 flow-root overflow-y-auto w-full">
            <ul className="w-full">
              {data?.rows?.map((complaint: any) => (
                <ChildComplaintCard key={complaint._id} complaint={complaint} />
              ))}
            </ul>
          </div>
        ) : (
          <div className="flex min-h-[50vh] items-center justify-center">
            <div className="text-center">
              <DocumentTextIcon strokeWidth={1} className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-300" />
              <h3 className="mt-3 text-sm font-medium text-gray-900 dark:text-gray-100">No Child Complaints</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">This complaint has no child complaints.</p>
            </div>
          </div>
        )
       }
      </div>
      {!!searchParams.id?.length && (
        <>
          <ViewComplaintContainer
            open={modal === "view"}
            setOpen={(val: boolean) => setModal(val ? "view" : undefined)}
          />
        </>
      )}
    </>
  );
};

export const ChildComplaintCard = ({ complaint }: { complaint: any }) => {

  const dateFormat = "DD/MM/YYYY"

  return (
    <div
      className="relative border-b first:border-t border-x grid grid-cols-5 border-gray-300 cursor-pointer hover:bg-gray-50 duration-150 w-full text-left  gap-4 p-4"
      onClick={wrapClick(dispatchModal(complaint._id, "view"))}
    >
      <td className='whitespace-nowrap pr-6 flex flex-row items-center gap-4 text-sm text-gray-500'>
        <div className=''>
          <DocumentTextIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
        </div>
        <div>
          <div className='font-medium text-gray-900 dark:text-gray-100'>
            {complaint?.code || "N/A"}
          </div>
          <div className='font-medium text-gray-500 dark:text-gray-400'>
            {moment(complaint?.createdAt).format(dateFormat)}
          </div>
        </div>
      </td>
      <td className='whitespace-nowrap px-6 text-sm text-gray-500 overflow-hidden text-ellipsis'>
        <div className='text-gray-900 dark:text-gray-100 overflow-hidden text-ellipsis'>
          {complaint?.contactPerson?.fullName || "N/A"}
        </div>
        <div className='text-gray-500 dark:text-gray-500 overflow-hidden text-ellipsis'>
          {complaint?.contactPerson?.phoneNumber || "N/A"}
        </div>
      </td>
      <td className="whitespace-nowrap px-6 py-2 text-sm text-gray-500">
        <StatusBadge
          status={complaint?.origin}
          options={{}}
        />
      </td>
      <td className="col-span-2 whitespace-nowrap px-6 text-sm text-gray-500 overflow-hidden text-ellipsis">
        <div className="flex items-center">
          <div className="h-10 w-10 flex-shrink-0">
            <Avatar
              disabled={true}
              alt={displayUserName(complaint?.createdBy) || "N A"}
              src={complaint?.createdBy?.profileImageUrl || ""}
            />
          </div>
          <div className="ml-4">
            <div className="text-gray-900 dark:text-gray-100 overflow-hidden text-ellipsis">
              {displayUserName(complaint?.createdBy) || "N A"}
            </div>
            <div className="text-gray-500 dark:text-gray-400 overflow-hidden text-ellipsis">
              {complaint?.createdBy?.code || "N/A"}
            </div>
          </div>
        </div>
      </td>
    </div>
  );
};

export default ChildComplaintsContainer;
